// Gerente-only dashboard view — 4 widgets mandated by sales management.
// KPI focus: KOMPA CLUB (confirmed subscription payment) for revenue +
// advisor conversion; the Volume card keeps the classic "Totales /
// Convertidos" headline and adds a second descriptor row with paid
// KOMPA CLUB members this month.
//
// Layout:
//   Row 1: Consolidado Financiero KOMPA CLUB | Leads Totales Vs. Convertidos  (50/50)
//   Row 2: Pipeline General  (full width)
//   Row 3: Rendimiento de Asesores (conversión a KOMPA CLUB, %)
import { DollarSign, Users, Crown, GitBranch, TrendingUp } from 'lucide-react';

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const MONTHS_ES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

function HeaderKPI({ label, value, caption, icon: Icon, color, testId }) {
  return (
    <div data-testid={testId} className={`kpi-card kpi-${color}`}>
      <div className="kpi-header">
        <span className="kpi-label">{label}</span>
        <div className={`kpi-icon kpi-icon-${color}`}><Icon size={18} /></div>
      </div>
      <div className="kpi-value">{value}</div>
      {caption && <div className="kpi-delta up">{caption}</div>}
    </div>
  );
}

function VolumeKPI({ monthName, totalLeads, convertedAll, convertedMonth, kompaClubMonth, testId }) {
  // Classic headline (Totales / Convertidos) + 2-row descriptor:
  //   Row 1: "N convertidos en {mes}"  (any plan — registration volume)
  //   Row 2: "M miembros KOMPA CLUB en {mes}"  (paid subscriptions only)
  return (
    <div data-testid={testId} className="kpi-card kpi-blue">
      <div className="kpi-header">
        <span className="kpi-label">Leads Totales Vs. Convertidos</span>
        <div className="kpi-icon kpi-icon-blue"><Users size={18} /></div>
      </div>
      <div className="kpi-value">
        {Number(totalLeads || 0).toLocaleString('es-VE')} / {Number(convertedAll || 0).toLocaleString('es-VE')}
      </div>
      <div className="kpi-delta up" data-testid={`${testId}-row-converted`}>
        {Number(convertedMonth || 0).toLocaleString('es-VE')} convertidos en {monthName}
      </div>
      <div className="kpi-delta kpi-delta-club" data-testid={`${testId}-row-club`}>
        <Crown size={11} /> {Number(kompaClubMonth || 0).toLocaleString('es-VE')} miembros KOMPA CLUB en {monthName}
      </div>
    </div>
  );
}

function Bars({ items, formatValue, testId }) {
  const max = Math.max(...items.map(i => i.value), 1);
  return (
    <div data-testid={testId} className="bar-chart">
      {items.map((item, idx) => (
        <div key={item.id ?? `${item.label}-${idx}`} className="bar-row">
          <span className="bar-label">{item.label}</span>
          <div className="bar-track">
            <div
              className="bar-fill"
              style={{ width: `${(item.value / max) * 100}%`, background: item.color || 'var(--kompa-accent)' }}
            />
          </div>
          <span className="bar-val">{formatValue ? formatValue(item.value, item) : item.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function GerenteDashboard({ metrics }) {
  const monthName = MONTHS_ES[new Date().getMonth()];
  const totalLeads     = metrics.total_leads || 0;
  const convertedAll   = metrics.total_converted_all_time || 0;
  const convertedMonth = metrics.converted_this_month || 0;
  const kompaClubMonth = metrics.kompa_club_month || 0;
  // ganado_month_total now sums only KOMPA CLUB paid members (backend v2)
  const kompaClubRevenueMonth = metrics.ganado_month_total || 0;

  // Agent performance — KOMPA CLUB conversion rate, monthly (resets each 1st).
  // leads_kompa_club_month = paid subscription members closed this month.
  const agentStats = (metrics.agent_stats || []).filter(a => (a.leads_assigned_month || 0) > 0);
  const agentItems = agentStats
    .map(a => ({
      id: a.id,
      label: a.name,
      value: a.conversion_rate_month || 0,
      assigned: a.leads_assigned_month || 0,
      registered: a.leads_registered_month || 0,
      kompaClub: a.leads_kompa_club_month || 0,
      color: '#b45309',
    }))
    .sort((a, b) => b.value - a.value);

  const pipelineItems = (metrics.pipeline_stages || []).map(s => ({
    label: s.label, value: s.count || 0, color: s.color,
  }));

  return (
    <div data-testid="gerente-dashboard" className="dashboard-page gerente-dashboard">
      {/* Row 1 — KOMPA CLUB Financial + Volume (50/50) */}
      <div className="dash-grid-2">
        <HeaderKPI
          testId="gerente-kpi-financiero"
          label={`Consolidado KOMPA CLUB · ${monthName}`}
          value={fmt(kompaClubRevenueMonth)}
          caption={`${kompaClubMonth} suscripciones KOMPA CLUB este mes`}
          icon={DollarSign}
          color="green"
        />
        <VolumeKPI
          testId="gerente-kpi-volumen"
          monthName={monthName}
          totalLeads={totalLeads}
          convertedAll={convertedAll}
          convertedMonth={convertedMonth}
          kompaClubMonth={kompaClubMonth}
        />
      </div>

      {/* Row 2 — Pipeline General (full width) */}
      <div data-testid="gerente-card-pipeline" className="dash-card" style={{ marginTop: 20 }}>
        <h3>
          <GitBranch size={16} /> Pipeline General
          <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
            distribución actual · 6 etapas
          </span>
        </h3>
        {pipelineItems.some(p => p.value > 0)
          ? <Bars items={pipelineItems} testId="gerente-bars-pipeline" />
          : <p className="text-muted">Sin leads en el sistema.</p>}
      </div>

      {/* Row 3 — Rendimiento de Asesores (KOMPA CLUB conversion %) */}
      <div data-testid="gerente-card-agentes" className="dash-card" style={{ marginTop: 20 }}>
        <h3>
          <TrendingUp size={16} /> Rendimiento de Asesores
          <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
            conversión a KOMPA CLUB · {monthName}
          </span>
        </h3>
        {agentItems.length > 0
          ? (
            <>
              <Bars
                items={agentItems}
                formatValue={(v, item) => `${v}% (${item.kompaClub}/${item.assigned} · reg ${item.registered})`}
                testId="gerente-bars-agentes"
              />
              <div className="quick-stats" style={{ marginTop: 12 }}>
                <div className="qs-row">
                  <span>Top asesor KOMPA CLUB</span>
                  <strong>
                    {agentItems[0].label} · {agentItems[0].value}% conversión
                  </strong>
                </div>
              </div>
            </>
          )
          : <p className="text-muted">Sin leads asignados este mes — el ranking se reinicia el 1° del próximo mes.</p>}
      </div>
    </div>
  );
}
